import { get, post, getToken, upload } from "../../api/axiosApi";
import { env } from "utils/env";
import { objToQueryString } from "utils/functions";

const url = env.REACT_APP_LEASING_API_URL;
const lsngUrl = env.REACT_APP_LSNG_API_URL;

const getBanks = search => {
  return get(`${url}/dictionaries/banks?search=${search}`);
};
const getActivityFieldsTypes = search => {
  return get(`${url}/dictionaries/activity-fields?search=${search}`);
};

const uploadOCRFile = (formData, progressCallback) => {
  return upload(`${url}/Partners/uploadOCRFile`, formData, progressCallback);
};

const getPartnerTypes = () => {
  return get(`${url}/dictionaries/partner-types`);
};

const getLocations = () => {
  return get(`${url}/Partners/locations`);
};

const getPersons = search => {
  return get(`${url}/dictionaries/persons?search=${search}`);
};

const getPartnerNationalityId = (uniqueId, isExternal) => {
  return get(`${url}/Partners/getNationalityId/${uniqueId}?isExternal=${isExternal}`);
};

const getSegregatedPersons = search => {
  return get(`${url}/dictionaries/persons?search=${search}`);
};

const getPartners = search => {
  return get(`${url}/Partners/partners?search=${search}`);
};

const exportPartners = filters => {
  return post(`${url}/Partners/export/list`, { ...filters });
};

const buildPartnerExportDownloadUrl = (filePath, fileName) => {
  return `${url}/Partners/export/file/${encodeURIComponent(filePath)}/${encodeURIComponent(
    fileName
  )}?access_token=${getToken()}`;
};

const getPositions = () => {
  return get(url + "/dictionaries/contactPersonRole/");
};

const getContactModes = () => {
  return get(url + "/dictionaries/contact-modes/");
};

const getCitizenships = () => {
  return get(url + "/dictionaries/citizenships/");
};

const getGenderTypes = () => {
  return get(url + "/dictionaries/gender-types/");
};

const getPersonDocumentTypes = () => {
  return get(url + "/dictionaries/person-document-types/");
};

const getMaritalStatuses = () => {
  return get(url + "/dictionaries/marital-statuses/");
};

const getSpecialRelations = () => {
  return get(url + "/dictionaries/special-relations/");
};

const getLegalForms = () => {
  return get(url + "/dictionaries/legal-forms/");
};

const getPartner = (partnerId, partnerTypeId) => {
  const query = objToQueryString({ partnerTypeId });
  return get(`${url}/partners/${partnerId}?${query}`);
};

const getPartnerDetails = uniqueId => {
  return get(`${url}/partners/${uniqueId}/lite`);
};

const getPartnerDetailsWithShareholder = uniqueId => {
  return get(`${url}/partners/${uniqueId}/partner-with-shareholders`);
};

const getCountries = () => {
  return get(`${url}/dictionaries/countries`);
};

const getCounties = countryId => {
  return get(`${url}/dictionaries/${countryId}/counties`);
};

const getCities = countyId => {
  return get(`${url}/dictionaries/${countyId}/cities`);
};

const getAddressTypes = () => {
  return get(`${url}/dictionaries/address-types`);
};

const getBankAccounts = partnerId => {
  return get(`${url}/partners/${partnerId}/bank-accounts`);
};

const getPartnerBankAccountsSummary = partnerId => {
  return get(`${url}/partners/${partnerId}/bank-accounts-summary`);
};

const getActivityFields = partnerId => {
  return get(`${url}/partners/${partnerId}/activity-fields`);
};

const getPartnerActivityFieldsSummary = partnerId => {
  return get(`${url}/partners/${partnerId}/activity-fields-summary`);
};

const getPartnerContactModes = partnerId => {
  return get(`${url}/partners/${partnerId}/contact-modes`);
};

const getContactInfos = partnerId => {
  return get(`${url}/partners/${partnerId}/contact-infos`);
};

const getPartnerContactInfoSummary = partnerId => {
  return get(`${url}/partners/${partnerId}/contact-info-summary`);
};

const getAddressList = partnerId => {
  return get(`${url}/partners/${partnerId}/addresses`);
};

const getPartnerAddressesSummary = partnerId => {
  return get(`${url}/partners/${partnerId}/partner-addresses-summary`);
};

const getShareholders = partnerId => {
  return get(`${url}/partners/${partnerId}/shareholders/2`);
};

const getProperties = partnerId => {
  return get(`${url}/partners/${partnerId}/properties`);
};

const getConsents = partnerId => {
  return get(`${url}/partners/${partnerId}/consents`);
};

const getCurrencies = () => {
  return get(`${url}/dictionaries/currencies`);
};

const getBankAccountTypes = () => {
  return get(`${url}/dictionaries/bank-account-types`);
};

const getPropertyDetails = propertyId => {
  return get(`${url}/dictionaries/properties/${propertyId}/property-details`);
};

const saveCompany = company => {
  return post(`${url}/partners/company`, company);
};

const saveIndividual = person => {
  return post(`${url}/partners/individual`, person);
};

const saveProperties = (partnerId, properties) => {
  return post(`${url}/partners/${partnerId}/properties/bulk`, { properties: properties });
};

const savePartnerLogo = (partnerId, partnerLogo) => {
  return post(`${url}/partners/${partnerId}/logo`, { partnerId: partnerLogo.partnerId, logoUrl: partnerLogo.logoUrl });
};

const saveAddresses = (partnerId, partnerAddresses) => {
  return post(`${url}/partners/${partnerId}/addresses/bulk`, { addresses: partnerAddresses });
};

const saveBankAccounts = (partnerId, bankAccounts) => {
  return post(`${url}/partners/${partnerId}/bank-accounts/bulk`, { bankAccounts: bankAccounts });
};
const saveActivityFields = (partnerId, activityFields) => {
  return post(`${url}/partners/${partnerId}/activity-fields/bulk`, { activityFields: activityFields });
};

const saveContactModes = (partnerId, contactModes) => {
  return post(`${url}/partners/${partnerId}/contact-modes/bulk`, { contactModes: contactModes });
};

const saveContactInfos = (partnerId, contactInfos) => {
  return post(`${url}/partners/${partnerId}/contact-infos/bulk`, { contactInfos: contactInfos });
};

const saveShareholders = (partnerId, shareholders) => {
  return post(`${url}/partners/${partnerId}/shareholders/bulk`, { shareholders: shareholders });
};

const saveConsents = (partnerId, consents) => {
  return post(`${url}/partners/${partnerId}/consents/bulk`, { consents: consents });
};

const getScoringGrids = partnerId => {
  return get(`${lsngUrl}/leasingdocument/${partnerId}/scoring`);
};

const getScoringCategoryOptions = () => {
  return get(`${lsngUrl}/leasingdocument/scoring/options`);
};

const getDimensionPeriods = () => {
  return get(`${lsngUrl}/dimensionperiods`);
};

const getShareholdersSummary = partnerId => {
  return get(`${url}/partners/${partnerId}/shareholders-summary`);
};

const getDealers = partnerId => {
  return get(`${url}/partners/${partnerId}/dealers`);
};

const getDealerContactModes = (partnerId, dealerId) => {
  return get(`${url}/partners/${partnerId}/dealer-contact-modes/${dealerId}`);
};

const getDealerContactInfos = (partnerId, dealerId) => {
  return get(`${url}/partners/${partnerId}/dealer-contact-infos/${dealerId}`);
};

const saveDealerContactModes = (partnerId, dealerContactModes) => {
  return post(`${url}/partners/${partnerId}/dealer-contact-modes/bulk`, { dealerContactModes: dealerContactModes });
};

const saveDealerContactInfos = (partnerId, dealerContactInfos) => {
  return post(`${url}/partners/${partnerId}/dealer-contact-infos/bulk`, { dealerContactInfos: dealerContactInfos });
};

const getPartnerLogs = (filters, page, pageSize) => {
  return get(`${url}/partners/logs?` + objToQueryString({ ...filters, page, pageSize }));
};

const getPartnerLog = partnerLogId => {
  return get(`${url}/partners/logs/${partnerLogId}`);
};

const importPartnerBalanceSheet = (balanceSheetId, file) => {
  return post(`${url}/partners/import-partner-balance-sheet`, { balanceSheetId, file });
};

const getPropertyByCode = propertyCode => {
  return get(`${url}/dictionaries/properties/${propertyCode}/detail`);
};

const exp = {
  getActivityFieldsTypes,
  getActivityFields,
  getPartners,
  exportPartners,
  buildPartnerExportDownloadUrl,
  uploadOCRFile,
  getPartnerTypes,
  getLocations,
  getPersons,
  getPositions,
  getContactModes,
  getCitizenships,
  getGenderTypes,
  getPersonDocumentTypes,
  getMaritalStatuses,
  getSpecialRelations,
  getLegalForms,
  getPartner,
  getPartnerDetails,
  getCountries,
  getCounties,
  getCities,
  getAddressTypes,
  getBankAccounts,
  getCurrencies,
  getBankAccountTypes,
  getBanks,
  getConsents,
  getPropertyDetails,
  getPartnerContactModes,
  getContactInfos,
  getAddressList,
  getShareholders,
  getProperties,
  saveCompany,
  saveIndividual,
  saveAddresses,
  saveBankAccounts,
  saveProperties,
  savePartnerLogo,
  saveContactModes,
  saveContactInfos,
  saveShareholders,
  saveConsents,
  saveActivityFields,
  getPartnerContactInfoSummary,
  getPartnerActivityFieldsSummary,
  getPartnerBankAccountsSummary,
  getPartnerAddressesSummary,
  getScoringGrids,
  getScoringCategoryOptions,
  getDimensionPeriods,
  getShareholdersSummary,
  getDealers,
  getPartnerDetailsWithShareholder,
  getSegregatedPersons,
  getPartnerLogs,
  getPartnerLog,
  getPartnerNationalityId: getPartnerNationalityId,
  getDealerContactModes,
  getDealerContactInfos,
  saveDealerContactModes,
  saveDealerContactInfos,
  importPartnerBalanceSheet,
  getPropertyByCode
};

export default exp;
